import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import Navbar from '@/components/Navbar';
import { toast } from 'sonner';
import { ChefHat, Plus, Pencil, Trash2, ImageIcon, ListChecks, CookingPot } from 'lucide-react';

const CATEGORIAS = [
  { value: 'desayuno', label: '🍳 Desayuno' },
  { value: 'comida', label: '🍲 Comida' },
  { value: 'cena', label: '🌙 Cena' },
  { value: 'snack', label: '🥜 Snack' },
];

const catLabel = (v) => CATEGORIAS.find(c => c.value === v)?.label || v;

const EMPTY_FORM = { titulo: '', categoria: 'comida', ingredientes: '', preparacion: '' };

export default function Recetas() {
  const { user } = useAuth();
  const [recetas, setRecetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas');
  const [verReceta, setVerReceta] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchRecetas = async () => {
    try {
      const res = await axios.get(`${API}/recetas`);
      setRecetas(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { fetchRecetas(); }, []);

  const puedeGestionar = (r) => user?.role === 'entrenador' || r.creado_por === user?.id;

  const openNueva = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFotoFile(null);
    setFotoPreview(null);
    setFormOpen(true);
  };

  const openEditar = (r) => {
    setEditingId(r.id);
    setForm({ titulo: r.titulo, categoria: r.categoria, ingredientes: r.ingredientes, preparacion: r.preparacion });
    setFotoFile(null);
    setFotoPreview(r.foto_url || null);
    setVerReceta(null);
    setFormOpen(true);
  };

  const onFotoChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFotoFile(f);
    setFotoPreview(URL.createObjectURL(f));
  };

  const guardar = async () => {
    if (!form.titulo.trim() || !form.ingredientes.trim() || !form.preparacion.trim()) {
      toast.error('Título, ingredientes y preparación son obligatorios');
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('titulo', form.titulo);
      fd.append('categoria', form.categoria);
      fd.append('ingredientes', form.ingredientes);
      fd.append('preparacion', form.preparacion);
      if (fotoFile) fd.append('foto', fotoFile);
      if (editingId) await axios.put(`${API}/recetas/${editingId}`, fd);
      else await axios.post(`${API}/recetas`, fd);
      toast.success(editingId ? 'Receta actualizada' : '¡Receta publicada para todo el Klan! 🍽️');
      setFormOpen(false);
      fetchRecetas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar la receta');
    } finally {
      setSaving(false);
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar esta receta?')) return;
    try {
      await axios.delete(`${API}/recetas/${id}`);
      toast.success('Receta borrada');
      setVerReceta(null);
      fetchRecetas();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al borrar');
    }
  };

  const visibles = filtro === 'todas' ? recetas : recetas.filter(r => r.categoria === filtro);

  return (
    <>
      <Navbar />
      <div className="min-h-screen pb-24 px-4 sm:px-6 lg:px-8 pt-6">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gold flex items-center gap-3">
              <ChefHat className="h-8 w-8" /> Recetas del Klan
            </h1>
            <p className="text-gray-400 mt-1">Comparte tus recetas con el resto del Klan</p>
          </div>
          <Button onClick={openNueva} className="btn-primary-mk" data-testid="btn-nueva-receta">
            <Plus className="h-4 w-4 mr-2" /> Nueva receta
          </Button>
        </div>

        {/* Filtro por categoría */}
        <div className="flex flex-wrap gap-2 mb-6">
          {[{ value: 'todas', label: 'Todas' }, ...CATEGORIAS].map(c => (
            <button
              key={c.value}
              onClick={() => setFiltro(c.value)}
              className={`px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${filtro === c.value ? 'bg-gold text-black border-gold' : 'bg-gray-800/60 border-gray-700 text-gray-300 hover:text-white'}`}
              data-testid={`filtro-receta-${c.value}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-gray-400">Cargando recetas...</p>
        ) : visibles.length === 0 ? (
          <div className="text-center py-16 text-gray-400" data-testid="recetas-vacio">
            <ChefHat className="h-12 w-12 mx-auto mb-3 opacity-40" />
            <p>Aún no hay recetas{filtro !== 'todas' ? ' en esta categoría' : ''}. ¡Sé el primero en compartir una!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visibles.map(r => (
              <Card
                key={r.id}
                onClick={() => setVerReceta(r)}
                className="bg-gray-900/70 border-gray-800 hover:border-gold/50 transition-colors cursor-pointer overflow-hidden"
                data-testid={`receta-card-${r.id}`}
              >
                {r.foto_url ? (
                  <img src={r.foto_url} alt={r.titulo} className="w-full h-44 object-cover" />
                ) : (
                  <div className="w-full h-44 bg-gray-800/60 flex items-center justify-center">
                    <ImageIcon className="h-10 w-10 text-gray-600" />
                  </div>
                )}
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="text-white font-bold text-base leading-snug">{r.titulo}</h3>
                    <span className="text-xs bg-gold/15 text-gold border border-gold/30 rounded-full px-2 py-0.5 whitespace-nowrap">{catLabel(r.categoria)}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Por {r.creado_por_nombre || '—'}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modal detalle */}
        <Dialog open={!!verReceta} onOpenChange={(o) => !o && setVerReceta(null)}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
            {verReceta && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-gold text-xl flex items-center justify-between gap-2 pr-6">
                    {verReceta.titulo}
                    <span className="text-xs bg-gold/15 text-gold border border-gold/30 rounded-full px-2 py-0.5 whitespace-nowrap font-normal">{catLabel(verReceta.categoria)}</span>
                  </DialogTitle>
                </DialogHeader>
                {verReceta.foto_url && (
                  <img src={verReceta.foto_url} alt={verReceta.titulo} className="w-full max-h-72 object-cover rounded-lg" />
                )}
                <div className="space-y-4">
                  <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4">
                    <h3 className="flex items-center gap-2 text-green-400 font-semibold mb-2">
                      <ListChecks className="h-5 w-5" /> Ingredientes
                    </h3>
                    <p className="text-gray-200 whitespace-pre-wrap text-sm" data-testid="receta-ingredientes">{verReceta.ingredientes}</p>
                  </div>
                  <div className="bg-gray-800/60 border border-gray-700 rounded-lg p-4">
                    <h3 className="flex items-center gap-2 text-gold font-semibold mb-2">
                      <CookingPot className="h-5 w-5" /> Preparación
                    </h3>
                    <p className="text-gray-200 whitespace-pre-wrap text-sm" data-testid="receta-preparacion">{verReceta.preparacion}</p>
                  </div>
                  <p className="text-xs text-gray-500">Receta de <span className="text-gold">{verReceta.creado_por_nombre || '—'}</span></p>
                  {puedeGestionar(verReceta) && (
                    <div className="flex gap-2">
                      <Button variant="outline" className="flex-1 border-gold/40 text-gold hover:bg-gold/10 h-9 text-sm"
                        onClick={() => openEditar(verReceta)} data-testid="btn-editar-receta">
                        <Pencil className="h-4 w-4 mr-1" /> Editar
                      </Button>
                      <Button variant="outline" className="flex-1 border-red-500/40 text-red-400 hover:bg-red-900/20 h-9 text-sm"
                        onClick={() => borrar(verReceta.id)} data-testid="btn-borrar-receta">
                        <Trash2 className="h-4 w-4 mr-1" /> Borrar
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal crear/editar */}
        <Dialog open={formOpen} onOpenChange={setFormOpen}>
          <DialogContent className="bg-gray-900 text-white border-gray-700 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-gold flex items-center gap-2">
                <ChefHat className="h-5 w-5" /> {editingId ? 'Editar receta' : 'Nueva receta'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Título *</Label>
                <Input value={form.titulo} onChange={(e) => setForm(f => ({ ...f, titulo: e.target.value }))}
                  placeholder="Ej: Tortitas de avena y plátano" className="bg-gray-800 border-gray-700" data-testid="receta-titulo" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Categoría *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIAS.map(c => (
                    <button key={c.value} onClick={() => setForm(f => ({ ...f, categoria: c.value }))}
                      className={`rounded-lg px-3 py-2 text-sm font-semibold border transition-colors ${form.categoria === c.value ? 'bg-gold/20 border-gold text-gold' : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'}`}
                      data-testid={`receta-cat-${c.value}`}>
                      {c.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Foto {editingId ? '(deja vacío para mantener la actual)' : '(opcional)'}</Label>
                <input type="file" accept="image/*" onChange={onFotoChange}
                  className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gold file:text-black file:font-semibold file:cursor-pointer"
                  data-testid="receta-foto" />
                {fotoPreview && <img src={fotoPreview} alt="preview" className="w-full h-40 object-cover rounded-lg mt-2" />}
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Ingredientes * (uno por línea)</Label>
                <textarea value={form.ingredientes} onChange={(e) => setForm(f => ({ ...f, ingredientes: e.target.value }))}
                  placeholder={'2 huevos\n100g de avena\n1 plátano maduro'} rows={5}
                  className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm outline-none focus:border-gold resize-y"
                  data-testid="receta-ingredientes-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-gray-400 text-xs">Preparación *</Label>
                <textarea value={form.preparacion} onChange={(e) => setForm(f => ({ ...f, preparacion: e.target.value }))}
                  placeholder={'1. Tritura el plátano con los huevos\n2. Añade la avena y mezcla\n3. Cocina a fuego medio 2 min por lado'} rows={6}
                  className="w-full bg-gray-800 border border-gray-700 rounded-md px-3 py-2 text-sm outline-none focus:border-gold resize-y"
                  data-testid="receta-preparacion-input" />
              </div>
              <Button onClick={guardar} disabled={saving} className="w-full btn-primary-mk" data-testid="btn-guardar-receta">
                {saving ? 'Guardando...' : editingId ? 'Guardar cambios' : 'Publicar receta'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </>
  );
}
